import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { FieldDescription } from 'components/FieldDescription';
import { FieldError } from 'components/FieldError';
import { FieldLabel } from 'components/FieldLabel';
import React, { useId } from 'react';

import type { FormComponentWrapperProps } from './types';

export const FormComponentWrapper: React.FC<FormComponentWrapperProps> = ({
  children,
  description,
  error,
  label,
  className = '',
  message,
  optional,
  id,
  disabled,
}) => {
  const generatedID = useId();
  const idToUse = id ?? generatedID;
  const formDescriptionId = `form-description-${idToUse}`;
  const formMessageId = `form-message-${idToUse}`;
  const formErrorId = `form-error-${idToUse}`;
  const formLabelId = `form-label-${idToUse}`;
  const wrapperClassname = cn(['flex', 'flex-col', 'gap-1'], className);

  return (
    <div
      className={wrapperClassname}
      id={`form-item-${idToUse}`}
      aria-describedby={
        !error ? `${formDescriptionId}` : `${formDescriptionId} ${formErrorId}`
      }
      aria-invalid={!!error}
    >
      {label || optional ? (
        <div className="flex items-center" data-testid={formLabelId}>
          {label && (
            <FieldLabel
              htmlFor={idToUse}
              error={Boolean(error?.message)}
              disabled={!!disabled}
            >
              {label}
            </FieldLabel>
          )}
          {optional && (
            <Text
              variant="captionRegular"
              className="text-color-comp-text-field-helper-text-enabled grow text-right"
            >
              Optional
            </Text>
          )}
        </div>
      ) : null}
      {description ? (
        <FieldDescription id={formDescriptionId}>
          {description}
        </FieldDescription>
      ) : null}
      {children}
      {error?.message ? (
        <FieldError id={formErrorId} errorMessage={error.message} />
      ) : null}
      {!error?.message && message ? (
        <FieldDescription id={formMessageId}>{message}</FieldDescription>
      ) : null}
    </div>
  );
};
